import { useEffect, useState } from "react";
import { getMeQuiet, getTerms, isNotOffered, type MlsMe } from "./api";

/**
 * Is MLS access offered on this deployment? Asked ONCE per app load, not once per screen.
 *
 * WHY A MODULE-LEVEL ANSWER. `AppShell` mounts on every screen, so a probe inside an effect would
 * send a request on every navigation just to rediscover a server flag. The server exposes no
 * capability payload for this flag (`/api/me` carries only the user), so the cheapest correct
 * signal is the feature's own `/terms` route, which 404s while `VALUBRIEF_MLS_ENABLED` is
 * off. The first caller starts the request; every later caller shares its answer.
 *
 * WHAT IS REMEMBERED. Only a definitive answer: 200 (offered) or 404 (not offered). Anything else
 * (not signed in yet, server down) reads as "not offered" for now and is asked again on the next
 * mount, so a link never appears before the server has said the route will serve, and a transient
 * failure does not hide it for the rest of the session. Flipping the server flag takes effect on
 * the next page load.
 */
let answer: boolean | null = null;
let pending: Promise<boolean> | null = null;

function probe(): Promise<boolean> {
  if (answer !== null) return Promise.resolve(answer);
  if (pending) return pending;
  pending = getTerms()
    .then(() => { answer = true; return true; })
    .catch((e: unknown) => {
      if (isNotOffered(e)) answer = false;
      return false;
    })
    .finally(() => { pending = null; });
  return pending;
}

/* The caller's own `/api/mls-access/me` (membership, manager, the four-part gate), asked at most
 * once per app load and ONLY after the feature has said it is offered, so a dark deployment never
 * sends it. Shared by the nav (manager links) and the comp step's MLS row. */
let meAnswer: MlsMe | null = null;
let mePending: Promise<MlsMe | null> | null = null;

function probeMe(): Promise<MlsMe | null> {
  if (meAnswer !== null) return Promise.resolve(meAnswer);
  if (mePending) return mePending;
  mePending = getMeQuiet()
    .then((m) => { meAnswer = m; return m; })
    .catch(() => null)
    .finally(() => { mePending = null; });
  return mePending;
}

/** Test seam: forget the remembered answers, so each test starts as a fresh app load. */
export function resetMlsOfferedForTests(): void {
  answer = null;
  pending = null;
  meAnswer = null;
  mePending = null;
}

export function useMlsOffered(enabled = true): boolean {
  const [offered, setOffered] = useState(answer === true);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void probe().then((v) => { if (!cancelled) setOffered(v); });
    return () => { cancelled = true; };
  }, [enabled]);
  return enabled && offered;
}

/** The caller's own MLS card, once the feature is offered; null while dark, loading or failed. */
export function useMlsMe(enabled = true): MlsMe | null {
  const offered = useMlsOffered(enabled);
  const [me, setMe] = useState<MlsMe | null>(meAnswer);
  useEffect(() => {
    if (!offered) return;
    let cancelled = false;
    void probeMe().then((m) => { if (!cancelled) setMe(m); });
    return () => { cancelled = true; };
  }, [offered]);
  return offered ? me : null;
}
